import React, { memo, useCallback, useMemo, useState } from 'react';
import { LayoutChangeEvent, ScrollView, StyleSheet } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { runOnJS } from 'react-native-reanimated';

import { Text } from '@/components/TextComponent';
import { EntryCardShareButton, SafetyShadow } from '@/components/entry-card';
import { Box, Column, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { EntryItemPick } from '@/feature/entries-pickem/components/EntriesList';
import { common } from '@/styles/styles';
import { EventStatus } from '@/types/api.generated';

import { useLiveIndicatorAnimation } from '../hooks/use-live-indicator-animation';
import { PickCircleWithStatus } from './PickCircleWithStatus';

/** Width of the safety-shadow gradient that fades the trailing avatars into the label. */
const GRADIENT_WIDTH = 68;

type Props = {
    picks: EntryItemPick[];
    label: string;
    sublabel?: string;
    onSharePress?: () => void;
    onCardPress?: () => void;
};

/** Renders the horizontally scrolling row of player pick circles (with live status), a fading gradient, the entry
 *  label/sublabel (turned red with an animated underline while picks are live), and an optional share button. */
export const PicksEntryCardBottom = memo(({ picks, label, sublabel, onSharePress, onCardPress }: Props) => {
    const hasLiveEvents = picks.some(pick => pick.eventStatus === EventStatus.InProgress);
    const allPicksHaveResults = picks.every(pick => pick.result);
    const showLiveStyling = hasLiveEvents && !allPicksHaveResults;

    // The ScrollView blocks the card tap in this area — racing the native scroll gesture
    // against a tap lets a still finger navigate while a moving finger to scroll.
    const scrollOrTapGesture = useMemo(
        () =>
            Gesture.Race(
                Gesture.Native(),
                Gesture.Tap()
                    .maxDistance(10)
                    .enabled(!!onCardPress)
                    .onEnd((_, success) => {
                        if (success && onCardPress) runOnJS(onCardPress)();
                    })
            ),
        [onCardPress]
    );

    const [indicatorWidth, setIndicatorWidth] = useState(-1);
    const onLayoutIndicator = useCallback((event: LayoutChangeEvent) => {
        setIndicatorWidth(event.nativeEvent.layout.width);
    }, []);
    const liveIndicatorStyle = useLiveIndicatorAnimation(indicatorWidth);

    const [overlayWidth, setOverlayWidth] = useState(0);
    const onLayoutOverlay = useCallback((event: LayoutChangeEvent) => {
        setOverlayWidth(event.nativeEvent.layout.width);
    }, []);
    // Pad by the opaque part of the overlay only (label + share), not the gradient — the last avatars
    // should still fade under the gradient as before, just no longer disappear behind the opaque label.
    const scrollContentStyle = useMemo(
        () => ({ paddingRight: Math.max(0, overlayWidth - GRADIENT_WIDTH) }),
        [overlayWidth]
    );

    return (
        <Row alignItems="center" width="100%">
            <Box flex={1} paddingLeft="s16">
                <GestureDetector gesture={scrollOrTapGesture}>
                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        keyboardShouldPersistTaps="always"
                        contentContainerStyle={scrollContentStyle}
                    >
                        {picks.map((pick, index) => (
                            <Box
                                key={`${pick.eventId}-${index}`}
                                style={index > 0 ? styles.pickCircleOverlap : undefined}
                                zIndex={picks.length - index}
                            >
                                <PickCircleWithStatus pick={pick} />
                            </Box>
                        ))}
                    </ScrollView>
                </GestureDetector>
            </Box>
            <Box
                position="absolute"
                right={0}
                top={0}
                bottom={0}
                flexDirection="row"
                alignItems="flex-start"
                style={common.zIndex1}
                onLayout={onLayoutOverlay}
            >
                <SafetyShadow style={styles.gradient} />
                <Column
                    backgroundColor={ds.colorToken.background.neutral.subtler}
                    paddingLeft="s4"
                    paddingRight="s16"
                    alignItems="flex-end"
                    justifyContent="flex-start"
                    paddingTop="s3"
                    gap="s2"
                    style={styles.fullHeight}
                >
                    <Text
                        variant="bodySmaller"
                        lineHeight={16}
                        fontWeight={showLiveStyling ? '500' : '400'}
                        color={showLiveStyling ? ds.colorToken.text.danger : 'neutral300'}
                        textAlign="right"
                    >
                        {label}
                    </Text>
                    {sublabel ? (
                        <Text variant="bodySmaller" lineHeight={16} color="neutral300" textAlign="right">
                            {sublabel}
                        </Text>
                    ) : null}
                    {showLiveStyling ? (
                        // +s2 on top of the column's s2 gap → 4px between the label and the indicator (Figma gap-4).
                        <Box
                            width="100%"
                            height={2}
                            marginTop="s2"
                            borderRadius="r4"
                            overflow="hidden"
                            onLayout={onLayoutIndicator}
                        >
                            <Animated.View style={[styles.liveIndicator, liveIndicatorStyle]} />
                        </Box>
                    ) : null}
                </Column>
                {onSharePress ? (
                    <Box
                        backgroundColor={ds.colorToken.background.neutral.subtler}
                        paddingRight="s16"
                        justifyContent="flex-start"
                        style={styles.fullHeight}
                    >
                        <EntryCardShareButton onPress={onSharePress} />
                    </Box>
                ) : null}
            </Box>
        </Row>
    );
});

const styles = StyleSheet.create({
    pickCircleOverlap: { marginLeft: -4 },
    gradient: { width: GRADIENT_WIDTH, height: '100%' },
    fullHeight: { height: '100%' },
    liveIndicator: { height: 2, width: '100%', backgroundColor: ds.colorValue.background.danger.strong },
});
